/**
 * Trust Degradation Dashboard Component
 * Pillar 3: Insider Risk & Trust Degradation Detection
 *
 * Features:
 * - Trust level distribution (CRITICAL, DEGRADED, FAIR, GOOD, EXCELLENT)
 * - Access change velocity tracking
 * - Trust trend visualization (DEGRADING, STABLE, IMPROVING)
 * - High-risk users with rapidly degrading trust
 * - Component score breakdown
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Paper,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserMetadata } from '../../../context/UserMetadataContext';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShieldIcon from '@mui/icons-material/Shield';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PeopleIcon from '@mui/icons-material/People';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import apiService from '../services/apiService';

// Trust level colors
const TRUST_COLORS = {
  CRITICAL: '#dc3545',
  DEGRADED: '#fd7e14',
  FAIR: '#ffc107',
  GOOD: '#20c997',
  EXCELLENT: '#28a745',
};

// Trend icons
const getTrendIcon = (trend) => {
  switch (trend) {
    case 'DEGRADING':
      return <TrendingDownIcon fontSize="small" color="error" />;
    case 'IMPROVING':
      return <TrendingUpIcon fontSize="small" color="success" />;
    case 'STABLE':
    default:
      return <RemoveIcon fontSize="small" color="disabled" />;
  }
};

const TrustDegradationDashboard = ({ onNavigateToTab }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { userMetadata } = useUserMetadata();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [summary, setSummary] = useState(null);
  const [highRiskUsers, setHighRiskUsers] = useState([]);
  const [velocityStats, setVelocityStats] = useState(null);

  // UI states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const tenantName = userMetadata?.company || 'trustx';

  // Initialize API service
  const initializeApi = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);
      apiService.setTenant(tenantName);
      return true;
    } catch (err) {
      console.error('Failed to initialize API service:', err);
      setError('Failed to authenticate. Please try logging in again.');
      return false;
    }
  }, [getAccessTokenSilently, tenantName]);

  // Fetch all trust degradation data
  const fetchTrustData = useCallback(async () => {
    setError(null);

    try {
      const initialized = await initializeApi();
      if (!initialized) return;

      // Fetch all data in parallel
      const [summaryRes, highRiskRes, velocityRes] = await Promise.all([
        apiService.getTrustDegradationSummary(),
        apiService.getTrustDegradationHighRisk({ minVelocity: 3, limit: 20 }),
        apiService.getAccessVelocityStats(),
      ]);

      if (summaryRes.success && summaryRes.data?.data) {
        setSummary(summaryRes.data.data);
      }

      if (highRiskRes.success && highRiskRes.data?.data) {
        setHighRiskUsers(highRiskRes.data.data);
      }

      if (velocityRes.success && velocityRes.data?.data) {
        setVelocityStats(velocityRes.data.data);
      }
    } catch (err) {
      console.error('Error fetching trust degradation data:', err);
      setError(err.message);
    }
  }, [initializeApi]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchTrustData();
      setLoading(false);
    };
    loadData();
  }, [fetchTrustData]);

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTrustData();
    setRefreshing(false);
  };

  // View user details
  const handleViewUser = async (userId) => {
    try {
      const response = await apiService.getTrustDegradationUserDetail(userId);
      if (response.success && response.data?.data) {
        setSelectedUser(response.data.data);
        setShowUserModal(true);
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  // Get trust level chip
  const getTrustLevelChip = (level) => {
    const colorMap = {
      CRITICAL: 'error',
      DEGRADED: 'warning',
      FAIR: 'info',
      GOOD: 'success',
      EXCELLENT: 'primary',
    };
    return (
      <Chip
        label={level}
        color={colorMap[level] || 'default'}
        size="small"
      />
    );
  };

  // Prepare pie chart data
  const getPieChartData = () => {
    if (!summary?.by_trust_level) return [];
    return Object.entries(summary.by_trust_level).map(([level, data]) => ({
      name: level,
      value: data.count,
      color: TRUST_COLORS[level] || '#6c757d',
    }));
  };

  // Prepare velocity chart data
  const getVelocityChartData = () => {
    if (!velocityStats?.top_users) return [];
    return velocityStats.top_users.slice(0, 8).map((user) => ({
      name: user.display_name || user.user_email?.split('@')[0] || 'Unknown',
      added: (user.groups_added || 0) + (user.apps_added || 0),
      removed: (user.groups_removed || 0) + (user.apps_removed || 0),
    }));
  };

  const getTrustScoreProgressColor = (score) => {
    if (score < 20) return 'error';
    if (score < 40) return 'warning';
    if (score < 60) return 'info';
    return 'success';
  };

  const getComponentProgressColor = (score) => {
    if (score < 40) return 'error';
    if (score < 70) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading trust degradation data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <ShieldIcon fontSize="large" color="primary" />
            <Typography variant="h5">Trust Degradation Detection</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Pillar 3: Insider Risk & Trust Degradation Detection
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={handleRefresh}
          disabled={refreshing}
          startIcon={<RefreshIcon sx={refreshing ? { animation: 'spin 1s linear infinite' } : {}} />}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid2 container spacing={2} sx={{ mb: 3 }}>
        {['CRITICAL', 'DEGRADED', 'FAIR', 'GOOD', 'EXCELLENT'].map((level) => {
          const data = summary?.by_trust_level?.[level] || { count: 0, avg_score: 0 };
          return (
            <Grid2 key={level} xs={12} sm={6} md={4} lg={2.4}>
              <Card
                sx={{
                  height: '100%',
                  borderLeft: `4px solid ${TRUST_COLORS[level]}`,
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: TRUST_COLORS[level] }}>
                    {data.count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {level}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    Avg Score: {data.avg_score}
                  </Typography>
                </CardContent>
              </Card>
            </Grid2>
          );
        })}
      </Grid2>

      {/* Charts Row */}
      <Grid2 container spacing={2} sx={{ mb: 3 }}>
        {/* Trust Distribution Pie Chart */}
        <Grid2 xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ShowChartIcon />
                <Typography variant="h6">Trust Level Distribution</Typography>
              </Stack>
            </Box>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={getPieChartData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {getPieChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid2>

        {/* Velocity Chart */}
        <Grid2 xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrendingUpIcon />
                <Typography variant="h6">Access Change Velocity (Top Users)</Typography>
              </Stack>
            </Box>
            <CardContent>
              {getVelocityChartData().length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={getVelocityChartData()} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="added" name="Added" fill="#28a745" stackId="a" />
                    <Bar dataKey="removed" name="Removed" fill="#dc3545" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                  No velocity data available
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* Trend Summary */}
      <Grid2 container spacing={2} sx={{ mb: 3 }}>
        <Grid2 xs={12} md={4}>
          <Card sx={{ height: '100%', bgcolor: 'rgba(220, 53, 69, 0.08)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingDownIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" color="error.main">{summary?.by_trend?.DEGRADING || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Degrading Trust</Typography>
            </CardContent>
          </Card>
        </Grid2>
        <Grid2 xs={12} md={4}>
          <Card sx={{ height: '100%', bgcolor: 'rgba(108, 117, 125, 0.08)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <RemoveIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h4" color="text.secondary">{summary?.by_trend?.STABLE || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Stable Trust</Typography>
            </CardContent>
          </Card>
        </Grid2>
        <Grid2 xs={12} md={4}>
          <Card sx={{ height: '100%', bgcolor: 'rgba(40, 167, 69, 0.08)' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main">{summary?.by_trend?.IMPROVING || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Improving Trust</Typography>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* High Risk Users Table */}
      <Card>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon color="error" />
            <Typography variant="h6">High Risk Users (Degrading Trust)</Typography>
          </Stack>
          <Chip label={`${highRiskUsers.length} users`} color="error" size="small" />
        </Box>
        <CardContent sx={{ p: 0 }}>
          {highRiskUsers.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Trust Score</TableCell>
                    <TableCell>Trust Level</TableCell>
                    <TableCell>Trend</TableCell>
                    <TableCell>7-Day Velocity</TableCell>
                    <TableCell>Key Concerns</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {highRiskUsers.map((user, index) => (
                    <TableRow key={`${user.user_id}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {user.display_name || user.user_email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.job_title || user.department || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LinearProgress
                            variant="determinate"
                            value={user.trust_score}
                            color={getTrustScoreProgressColor(user.trust_score)}
                            sx={{ width: 60, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">{user.trust_score}/100</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{getTrustLevelChip(user.trust_level)}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {getTrendIcon(user.trust_trend)}
                          <Typography variant="caption">{user.trust_trend || 'N/A'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          <Box component="span" sx={{ color: 'success.main' }}>
                            +{(user.velocity?.groups_added_7d || 0) + (user.velocity?.apps_added_7d || 0)}
                          </Box>
                          {' / '}
                          <Box component="span" sx={{ color: 'error.main' }}>
                            -{(user.velocity?.groups_removed_7d || 0) + (user.velocity?.apps_removed_7d || 0)}
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.risk_indicators?.slice(0, 2).map((indicator, i) => (
                            <Chip key={i} label={indicator} size="small" sx={{ fontSize: 10 }} />
                          ))}
                          {user.risk_indicators?.length > 2 && (
                            <Chip
                              label={`+${user.risk_indicators.length - 2}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 10 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<ChevronRightIcon />}
                          onClick={() => handleViewUser(user.user_id)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
              <PeopleIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography>No high-risk users detected</Typography>
              <Typography variant="caption">
                Users with degrading trust or high access velocity will appear here
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <Dialog open={showUserModal} onClose={() => setShowUserModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShieldIcon />
              <Typography variant="h6">Trust Score Details</Typography>
            </Stack>
            <IconButton onClick={() => setShowUserModal(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedUser && (
            <>
              <Grid2 container spacing={2} sx={{ mb: 3 }}>
                <Grid2 xs={12} md={6}>
                  <Typography variant="h6">{selectedUser.display_name || selectedUser.user_email}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedUser.user_email}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.job_title} - {selectedUser.department}
                  </Typography>
                </Grid2>
                <Grid2 xs={12} md={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="h3" sx={{ color: TRUST_COLORS[selectedUser.trust_level] }}>
                    {selectedUser.trust_score}/100
                  </Typography>
                  {getTrustLevelChip(selectedUser.trust_level)}
                </Grid2>
              </Grid2>

              <Typography variant="subtitle2" gutterBottom>Component Scores</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Component</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Weight</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { name: 'Privilege Creep', key: 'privilege_creep', weight: '25%' },
                      { name: 'Suspicious Activity', key: 'suspicious_activity', weight: '25%' },
                      { name: 'Excessive Access', key: 'excessive_access', weight: '20%' },
                      { name: 'Access Velocity', key: 'access_velocity', weight: '15%' },
                      { name: 'Dormant Risk', key: 'dormant_risk', weight: '15%' },
                    ].map((comp) => {
                      const score = selectedUser.components?.[comp.key] || 0;
                      return (
                        <TableRow key={comp.key}>
                          <TableCell>{comp.name}</TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <LinearProgress
                                variant="determinate"
                                value={score}
                                color={getComponentProgressColor(score)}
                                sx={{ flex: 1, height: 10, borderRadius: 5 }}
                              />
                              <Typography variant="caption">{score}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{comp.weight}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle2" gutterBottom>7-Day Access Velocity</Typography>
              <Grid2 container spacing={1} sx={{ mb: 3 }}>
                {[
                  { label: 'Groups Added', value: `+${selectedUser.velocity?.groups_added_7d || 0}`, color: 'success.main' },
                  { label: 'Groups Removed', value: `-${selectedUser.velocity?.groups_removed_7d || 0}`, color: 'error.main' },
                  { label: 'Apps Added', value: `+${selectedUser.velocity?.apps_added_7d || 0}`, color: 'success.main' },
                  { label: 'Apps Removed', value: `-${selectedUser.velocity?.apps_removed_7d || 0}`, color: 'error.main' },
                ].map(({ label, value, color }) => (
                  <Grid2 xs={6} md={3} key={label}>
                    <Card variant="outlined" sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="body2" sx={{ color }} fontWeight="bold">{value}</Typography>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Card>
                  </Grid2>
                ))}
              </Grid2>

              {selectedUser.ai_analysis?.indicators?.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Risk Indicators</Typography>
                  <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedUser.ai_analysis.indicators.map((indicator, i) => (
                      <Chip key={i} label={indicator} color="warning" size="small" />
                    ))}
                  </Box>
                </>
              )}

              {selectedUser.ai_analysis?.reasoning && (
                <>
                  <Typography variant="subtitle2" gutterBottom>AI Analysis</Typography>
                  <Alert severity="info">{selectedUser.ai_analysis.reasoning}</Alert>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUserModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default TrustDegradationDashboard;
